import React, { useState, useEffect, useCallback } from "react";
import { getToday, checkIn, checkOut, getPublicSettings, getHolidays } from "../../../services/attendanceApi";
import { useToast } from "./Toast";
import Icons from "./Icons";

/**
 * CheckInCard — today's attendance + Check In / Check Out.
 *
 * Features:
 *  - Live per-second "working since" timer
 *  - Late-by badge (from backend value)
 *  - Smart status banner ("On time ✅" / "Running late" / "Overtime 🎉")
 *  - Toast notifications on check-in/out
 *  - Confirm dialog before check-out
 *  - "All times in IST" label
 */
function CheckInCard({ onChange, onRequestCorrection }) {
  const { toast, confirm } = useToast();
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [wfh, setWfh] = useState(false);
  const [shift, setShift] = useState(null); // { shiftStart, shiftEnd, graceMinutes }
  // Day-type gate: when today is a weekend or a non-working holiday the
  // backend rejects check-in with a 400 — so we hide/disable the button.
  // { type: "WEEKEND" | "HOLIDAY" | "WORKING", holidayName?: string }
  const [dayType, setDayType] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getToday();
      // Backend returns { date, attendance: { check_in, check_out, status,
      // late_by, overtime_hours, is_wfh, ... } } (or null when not checked in).
      // Normalize to the flat camelCase shape this component uses.
      const raw = res && res.data ? res.data : res;
      const a = raw && raw.attendance ? raw.attendance : null;
      setToday(
        a
          ? {
              checkInTime: a.check_in ?? a.check_in_time ?? null,
              checkOutTime: a.check_out ?? a.check_out_time ?? null,
              status: a.status ?? null,
              lateBy: a.late_by ?? a.lateBy ?? null,
              overtimeHours: a.overtime_hours ?? a.overtimeHours ?? 0,
              isWfh: a.is_wfh ?? false,
              earlyLogout: a.early_logout ?? a.earlyLogout ?? false,
              earlyLogoutMinutes: a.early_logout_minutes ?? a.earlyLogoutMinutes ?? 0,
            }
          : null
      );
    } catch (err) {
      setError(err.message || "Could not load today's status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Read-only shift window for the employee (from public settings).
  // If the call fails, hide the shift line silently.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getPublicSettings();
        const d = res && res.data ? res.data : res;
        if (!d) return;
        if (alive) {
          setShift({
            shiftStart: d.shift_start ?? d.shiftStart ?? null,
            shiftEnd: d.shift_end ?? d.shiftEnd ?? null,
            graceMinutes: d.grace_minutes ?? d.graceMinutes ?? null,
          });
        }
      } catch {
        if (alive) setShift(null); // hide silently
      }
    })();
    return () => { alive = false; };
  }, []);

  // Determine today's day-type (weekend / holiday / working day).
  // Fetches public settings + holidays; if either fails we fall back to a
  // normal working day so the Check In button is never wrongly hidden.
  useEffect(() => {
    let alive = true;
    (async () => {
      // Compute "today" in IST (Asia/Kolkata) so the gate matches the backend.
      const istYmd = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }); // YYYY-MM-DD
      const istDow = new Date().toLocaleDateString("en-US", { timeZone: "Asia/Kolkata", weekday: "short" }); // Sat/Sun/...
      const isWeekend = istDow === "Sat" || istDow === "Sun";
      const year = Number(istYmd.slice(0, 4));
      try {
        const hRes = await getHolidays(year);
        const hData = hRes && hRes.data ? hRes.data : hRes;
        const holidays = Array.isArray(hData) ? hData : (hData && hData.holidays ? hData.holidays : []);
        // Find a holiday row matching today.
        const todayHoliday = holidays.find((h) => {
          const key = h.holiday_date || h.holidayDate || h.date;
          return key === istYmd;
        });
        const isWorkingHoliday =
          todayHoliday && (todayHoliday.is_working ?? todayHoliday.isWorking) === true;
        if (!alive) return;
        if (todayHoliday && !isWorkingHoliday) {
          // Non-working holiday — no check-in.
          setDayType({ type: "HOLIDAY", holidayName: todayHoliday.name || "Holiday" });
        } else if (isWeekend && !isWorkingHoliday) {
          // Weekend and NOT a working-weekend holiday.
          setDayType({ type: "WEEKEND" });
        } else {
          // Normal working day (or a working-weekend holiday).
          setDayType({ type: "WORKING" });
        }
      } catch {
        // Holiday call failed — fall back: only weekends can be inferred
        // locally. Be conservative and still show the button on weekends
        // (backend will reject if truly closed) by treating as WORKING.
        if (alive) setDayType({ type: "WORKING" });
      }
    })();
    return () => { alive = false; };
  }, []);

  // Live clock — per second while checked in (not checked out)
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const doCheckIn = async () => {
    const ok = await confirm("Check in now?", "Your check-in time will be recorded as the current time.");
    if (!ok) return;
    try {
      setActing(true); setError("");
      await checkIn(wfh);
      await load();
      onChange && onChange();
      toast.success("Checked in successfully");
    } catch (err) {
      toast.error(err.message || "Check-in failed");
    } finally { setActing(false); }
  };

  const doCheckOut = async () => {
    const ok = await confirm("Check out now?", "Your working hours for today will be finalized.");
    if (!ok) return;
    try {
      setActing(true); setError("");
      await checkOut();
      await load();
      onChange && onChange();
      toast.success("Checked out — have a great day!");
    } catch (err) {
      toast.error(err.message || "Check-out failed");
    } finally { setActing(false); }
  };

  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
  };

  // ── Derived values ──
  const workedMs = () => {
    if (!today || !today.checkInTime) return 0;
    const start = new Date(today.checkInTime).getTime();
    const end = today.checkOutTime ? new Date(today.checkOutTime).getTime() : now;
    return Math.max(0, end - start);
  };

  const workedText = () => {
    const ms = workedMs();
    if (!ms) return "—";
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    // Show seconds only while actively working (not checked out)
    if (today && today.checkOutTime) return `${h}h ${m}m`;
    return `${h}h ${m}m ${String(sec).padStart(2, "0")}s`;
  };

  // Late-by minutes — trust the backend value only.
  // (Client-side shift math is skipped until GET /settings is deployed,
  //  so we never show an inaccurate late-by based on default times.)
  const lateByMin = () => {
    if (!today) return 0;
    const v = today.lateBy ?? today.late_by ?? today.lateMinutes ?? today.late_minutes;
    return v ? Number(v) : 0;
  };

  const overtimeHrs = () => {
    const v = today && (today.overtimeHours ?? today.overtime_hours);
    return v ? Number(v) : 0;
  };

  // Early-logout flag/minutes — trust the backend value only.
  const earlyLogout = () => !!(today && (today.earlyLogout ?? today.early_logout));
  const earlyLogoutMin = () => {
    const v = today && (today.earlyLogoutMinutes ?? today.early_logout_minutes);
    return v ? Number(v) : 0;
  };

  const statusClass = (s) => {
    switch ((s || "").toUpperCase()) {
      case "PRESENT": return "atl-pill atl-pill--present";
      case "LATE": return "atl-pill atl-pill--late";
      case "HALF_DAY": return "atl-pill atl-pill--half";
      case "ABSENT": return "atl-pill atl-pill--absent";
      default: return "atl-pill";
    }
  };

  if (loading) {
    return <div className="atl-card atl-checkin"><div className="atl-skeleton" /></div>;
  }

  const checkedIn = today && today.checkInTime;
  const checkedOut = today && today.checkOutTime;
  const late = lateByMin();
  const ot = overtimeHrs();
  // Today is a non-working day when it's a weekend or a non-working holiday.
  // Falls back to false (button shown) until dayType resolves or if it fails.
  const isNonWorkingDay = !!(dayType && (dayType.type === "WEEKEND" || dayType.type === "HOLIDAY"));

  // Smart banner — driven by backend status (accurate, no client shift math).
  const st = ((today && today.status) || "").toUpperCase();
  let banner = null;
  if (!checkedIn) {
    banner = { cls: "atl-banner--warn", icon: Icons.clock, text: "You haven't checked in yet today." };
  } else if (checkedOut) {
    banner = { cls: "atl-banner--done", icon: Icons.check, text: "You're done for today. Well done!" };
  } else if (st === "LATE") {
    banner = { cls: "atl-banner--late", icon: Icons.late, text: late > 0 ? `You checked in ${late} min late.` : "You checked in late today." };
  } else if (ot > 0) {
    banner = { cls: "atl-banner--ot", icon: Icons.stats, text: "You're in overtime today." };
  } else {
    banner = { cls: "atl-banner--ok", icon: Icons.present, text: "You're on time. Have a productive day!" };
  }

  return (
    <div className="atl-card atl-checkin">
      <div className="atl-checkin__head">
        <h3>Today</h3>
        <span className="atl-checkin__date">
          {new Date().toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "long", day: "numeric", month: "short" })}
        </span>
      </div>

      {/* Read-only shift window (from public settings) */}
      {shift && shift.shiftStart && shift.shiftEnd && (
        <div className="atl-checkin__date atl-shiftinfo">
          Shift: {shift.shiftStart}–{shift.shiftEnd}
          {shift.graceMinutes != null ? ` · Grace ${shift.graceMinutes} min` : ""}
        </div>
      )}

      {error && <div className="atl-error">{error}</div>}

      {/* Smart status banner */}
      <div className={`atl-banner ${banner.cls}`}><span className="atl-banner__ic">{banner.icon}</span><span>{banner.text}</span></div>

      <div className="atl-checkin__status">
        {today && today.status ? (
          <span className={statusClass(today.status)}>{today.status}</span>
        ) : (
          <span className="atl-pill">Not checked in</span>
        )}
        {late > 0 && <span className="atl-lateby">{late} min late</span>}
        {checkedOut && earlyLogout() && (
          <span className="atl-pill atl-pill--early">
            {earlyLogoutMin() > 0 ? `Early logout (${earlyLogoutMin()} min)` : "Early logout"}
          </span>
        )}
      </div>

      <div className="atl-checkin__times">
        <div>
          <span className="atl-checkin__label">Check In</span>
          <span className="atl-checkin__val">{fmtTime(today && today.checkInTime)}</span>
        </div>
        <div>
          <span className="atl-checkin__label">Check Out</span>
          <span className="atl-checkin__val">{fmtTime(today && today.checkOutTime)}</span>
        </div>
        <div>
          <span className="atl-checkin__label">Worked</span>
          <span className={`atl-checkin__val ${checkedIn && !checkedOut ? "atl-live" : ""}`}>{workedText()}</span>
        </div>
      </div>

      <div className="atl-checkin__actions">
        {/* Weekend / holiday notice — shown instead of the Check In button
            when today is a non-working day (prevents the 400 on check-in). */}
        {!checkedIn && isNonWorkingDay && (
          <div className="atl-banner atl-banner--warn">
            <span className="atl-banner__ic">{Icons.clock}</span>
            <span>
              {dayType && dayType.type === "HOLIDAY"
                ? `Today is a holiday: ${dayType.holidayName}`
                : "Today is a weekend — no check-in required"}
            </span>
          </div>
        )}
        {!checkedIn && !isNonWorkingDay && (
          <div className="atl-field atl-field--check">
            <label>
              <input type="checkbox" checked={wfh} onChange={(e) => setWfh(e.target.checked)} disabled={acting} />
              Work from home
            </label>
          </div>
        )}
        {!checkedIn && !isNonWorkingDay && (
          <button className="atl-btn atl-btn--primary" onClick={doCheckIn} disabled={acting}>
            {acting ? "Checking in..." : "Check In"}
          </button>
        )}
        {checkedIn && !checkedOut && (
          <button className="atl-btn atl-btn--danger" onClick={doCheckOut} disabled={acting}>
            {acting ? "Checking out..." : "Check Out"}
          </button>
        )}
        {checkedIn && checkedOut && (
          <div className="atl-checkin__done"><span className="atl-checkin__doneic">{Icons.check}</span>You're done for today</div>
        )}
        {/* General helper — not tied to today's state. Lets the employee jump
            to the correction form to fix a forgotten check-out on any past day. */}
        <div className="atl-checkin__hint">
          Forgot to check out on a previous day?{" "}
          {onRequestCorrection ? (
            <button type="button" className="atl-link-btn" onClick={() => onRequestCorrection()}>
              Request a correction
            </button>
          ) : (
            <span>Use the Correction request form to add your check-out time (admin approval).</span>
          )}
        </div>
      </div>

      <div className="atl-tz"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18"/></svg> All times in IST (Asia/Kolkata)</div>
    </div>
  );
}

export default CheckInCard;